import React from 'react'
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { Table } from 'react-bootstrap'
import DateRangeFilter from '../../components/DateRangeFilter'
import EvidenceLightbox from '../../components/EvidenceLightbox'
import MuiFilterSelect, { DEFAULT_FILTER_VALUE } from '../../components/Filters/MuiFilterSelect'
import SearchInput from '../../components/SearchInput'
import SettlementDetailContextHeader from '../../components/SettlementDetailContextHeader'
import {
  SETTLEMENT_DETAILS_DEPOSITS_IMAGE_ARIA_PLURAL,
  SETTLEMENT_DETAILS_DEPOSITS_IMAGE_ARIA_PREFIX,
  SETTLEMENT_DETAILS_DEPOSITS_IMAGE_ARIA_SINGULAR,
  SETTLEMENT_PAYMENT_BREAKDOWN_BANK_FILTER_TITLE,
  SETTLEMENT_PAYMENT_BREAKDOWN_DATE_FILTER_TITLE,
  SETTLEMENT_PAYMENT_BREAKDOWN_DATE_RANGE_PLACEHOLDER,
  SETTLEMENT_PAYMENT_BREAKDOWN_EMPTY_STATE,
  SETTLEMENT_PAYMENT_BREAKDOWN_FILTER_LABEL,
  SETTLEMENT_PAYMENT_BREAKDOWN_IMAGE_ARIA_SUFFIX,
  SETTLEMENT_PAYMENT_BREAKDOWN_MISSING_VALUE,
  SETTLEMENT_PAYMENT_BREAKDOWN_PAYMENT_TYPE_FILTER_TITLE,
  SETTLEMENT_PAYMENT_BREAKDOWN_RESET_LABEL,
  SETTLEMENT_PAYMENT_BREAKDOWN_SEARCH_PLACEHOLDER,
  SETTLEMENT_PAYMENT_BREAKDOWN_STATUS_HEADER,
  SETTLEMENT_PAYMENT_BREAKDOWN_STATUS_TOOLTIP,
  SETTLEMENT_PAYMENT_BREAKDOWN_TABLE_HEADERS,
  SETTLEMENT_PAYMENT_BREAKDOWN_TITLE
} from '../../constants/settlementDetailsConstants'
import {
  UI_COLOR_ACCENT_ORANGE,
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_LINK,
  UI_COLOR_SURFACE_PRIMARY_SOFT,
  UI_COLOR_WHITE
} from '../../constants/uiColors'
import GoogleIcon from '../../icon/GoogleIcon'
import { formatMoney } from '../../helpers/utils'
import { getTimestampFromDateTime } from '../../helpers/formatDate'
import dayjs from '../../utils/dayjsConfig'
import {
  buildPaymentBreakdownRows,
  getPaymentStatusUI,
  normalizeText
} from '../helpers/detailsHelpers'

const renderPaymentStatus = (status) => {
  const paymentStatusUI = getPaymentStatusUI(status)

  if (!paymentStatusUI) return SETTLEMENT_PAYMENT_BREAKDOWN_MISSING_VALUE

  return (
    <div className="td-order-status">
      <div className={`order-status ${paymentStatusUI.className}`}>{paymentStatusUI.label}</div>
    </div>
  )
}

const renderPaymentBreakdownHeader = (header) => {
  if (header !== SETTLEMENT_PAYMENT_BREAKDOWN_STATUS_HEADER) return header

  return (
    <>
      {header}
      <Tooltip title={SETTLEMENT_PAYMENT_BREAKDOWN_STATUS_TOOLTIP} arrow placement="top">
        <span
          style={{
            cursor: 'help',
            display: 'inline-flex',
            marginLeft: 4,
            verticalAlign: 'text-bottom'
          }}
        >
          <GoogleIcon name="info" size={13} color={UI_COLOR_WHITE} weight={300} />
        </span>
      </Tooltip>
    </>
  )
}

export default function SettlementPaymentBreakdownTab({ settlement = null, actions = null }) {
  const [paymentTypeFilter, setPaymentTypeFilter] = React.useState(DEFAULT_FILTER_VALUE)
  const [bankFilter, setBankFilter] = React.useState(DEFAULT_FILTER_VALUE)
  const [paymentDateRange, setPaymentDateRange] = React.useState(null)
  const [searchInput, setSearchInput] = React.useState('')
  const [search, setSearch] = React.useState('')
  const [lightbox, setLightbox] = React.useState({ open: false, images: [], index: 0 })
  const [attachmentsDialog, setAttachmentsDialog] = React.useState({ open: false, files: [] })

  const rows = React.useMemo(() => buildPaymentBreakdownRows(settlement), [settlement])
  const filtersDisabled = rows.length === 0

  const paymentTypeOptions = React.useMemo(() => {
    return Array.from(new Set(rows.map((row) => row.paymentType).filter(Boolean))).map(
      (option) => ({
        value: option,
        label: option
      })
    )
  }, [rows])

  const bankOptions = React.useMemo(() => {
    return Array.from(new Set(rows.map((row) => row.bank).filter(Boolean))).map((option) => ({
      value: option,
      label: option
    }))
  }, [rows])

  const filteredRows = React.useMemo(() => {
    const paymentTypeValues =
      paymentTypeFilter?.mode === 'custom'
        ? paymentTypeFilter.values.map((value) => normalizeText(value))
        : []
    const bankValues =
      bankFilter?.mode === 'custom' ? bankFilter.values.map((value) => normalizeText(value)) : []
    const normalizedSearch = normalizeText(search)

    return rows.filter((row) => {
      const rowPaymentType = normalizeText(row.paymentType)
      const rowBank = normalizeText(row.bank)

      if (paymentTypeValues.length > 0 && !paymentTypeValues.includes(rowPaymentType)) {
        return false
      }

      if (bankValues.length > 0 && !bankValues.includes(rowBank)) {
        return false
      }

      if (Array.isArray(paymentDateRange) && paymentDateRange.length > 0) {
        const rowDate = dayjs(row?.date)
        if (!rowDate.isValid()) return false

        const start = dayjs(paymentDateRange[0]).startOf('day')
        const end = dayjs(paymentDateRange[paymentDateRange.length - 1]).endOf('day')

        if (rowDate.isBefore(start) || rowDate.isAfter(end)) {
          return false
        }
      }

      if (!normalizedSearch) return true

      return [row.chargeUid, row.reference]
        .filter(Boolean)
        .some((value) => normalizeText(value).includes(normalizedSearch))
    })
  }, [bankFilter, paymentDateRange, paymentTypeFilter, rows, search])

  const shouldShowReset = React.useMemo(() => {
    return (
      paymentTypeFilter?.mode === 'custom' ||
      bankFilter?.mode === 'custom' ||
      (Array.isArray(paymentDateRange) && paymentDateRange.length > 0) ||
      Boolean(search.trim()) ||
      Boolean(searchInput.trim())
    )
  }, [bankFilter, paymentDateRange, paymentTypeFilter, search, searchInput])

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value)
  }

  const handleClearSearch = () => {
    setSearchInput('')
    setSearch('')
  }

  const handleSearchEnter = (event) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    setSearch(searchInput)
  }

  const handleResetFilters = () => {
    setPaymentTypeFilter(DEFAULT_FILTER_VALUE)
    setBankFilter(DEFAULT_FILTER_VALUE)
    setPaymentDateRange(null)
    setSearchInput('')
    setSearch('')
  }

  const handleOpenAttachments = (files = []) => {
    const validFiles = files.filter((file) => file?.url)

    if (validFiles.length === 0) return

    if (validFiles.length === 1) {
      window.open(validFiles[0].url, '_blank', 'noopener,noreferrer')
      return
    }

    setAttachmentsDialog({ open: true, files: validFiles })
  }

  return (
    <Box>
      <SettlementDetailContextHeader
        title={SETTLEMENT_PAYMENT_BREAKDOWN_TITLE}
        settlement={settlement}
        actions={actions}
      />

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {SETTLEMENT_PAYMENT_BREAKDOWN_FILTER_LABEL}
      </Typography>

      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={3}>
        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" flex={1}>
          <GoogleIcon className="filter-icon-poc oc-icon-primary" name="page_info" size={22} />

          <MuiFilterSelect
            title={SETTLEMENT_PAYMENT_BREAKDOWN_PAYMENT_TYPE_FILTER_TITLE}
            options={paymentTypeOptions}
            value={paymentTypeFilter}
            onChange={setPaymentTypeFilter}
            width={220}
            dropdownWidth={280}
            matchTriggerWidth
            disabled={filtersDisabled || paymentTypeOptions.length === 0}
          />

          <MuiFilterSelect
            title={SETTLEMENT_PAYMENT_BREAKDOWN_BANK_FILTER_TITLE}
            options={bankOptions}
            value={bankFilter}
            onChange={setBankFilter}
            width={220}
            dropdownWidth={280}
            matchTriggerWidth
            disabled={filtersDisabled || bankOptions.length === 0}
          />

          <DateRangeFilter
            disabled={filtersDisabled}
            value={paymentDateRange}
            onChange={setPaymentDateRange}
            inputTitle={SETTLEMENT_PAYMENT_BREAKDOWN_DATE_FILTER_TITLE}
            placeholder={SETTLEMENT_PAYMENT_BREAKDOWN_DATE_RANGE_PLACEHOLDER}
          />
        </Box>

        <Box flexShrink={0} mt={0.6}>
          <SearchInput
            value={searchInput}
            onChange={handleSearchChange}
            onEnter={handleSearchEnter}
            onClear={handleClearSearch}
            placeholder={SETTLEMENT_PAYMENT_BREAKDOWN_SEARCH_PLACEHOLDER}
          />
        </Box>
      </Box>

      {shouldShowReset && (
        <Typography variant="body2" className="oc-reset-filter" onClick={handleResetFilters}>
          {SETTLEMENT_PAYMENT_BREAKDOWN_RESET_LABEL}
        </Typography>
      )}

      <Paper elevation={0} sx={{ overflow: 'hidden' }}>
        <Table hover responsive>
          <thead className="table-header-gray-mui">
            <tr>
              {SETTLEMENT_PAYMENT_BREAKDOWN_TABLE_HEADERS.map((header) => (
                <th
                  key={header}
                  className={
                    header === 'MONTO'
                      ? 'td-number-align-right'
                      : header === 'IMAGEN' || header === 'ADJUNTOS'
                        ? 'text-center'
                        : undefined
                  }
                >
                  {renderPaymentBreakdownHeader(header)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.length > 0 ? (
              filteredRows.map((row) => (
                <tr key={row.id}>
                  <td className="td-text-limited">
                    {row.date
                      ? getTimestampFromDateTime(row.date)
                      : SETTLEMENT_PAYMENT_BREAKDOWN_MISSING_VALUE}
                  </td>
                  <td className="td-text-limited">
                    {row.chargeUid ? (
                      <Link
                        component="button"
                        onClick={() => window.open(`/cobros/${row.chargeUid}`, '_blank')}
                      >
                        {row.chargeUid}
                      </Link>
                    ) : (
                      SETTLEMENT_PAYMENT_BREAKDOWN_MISSING_VALUE
                    )}
                  </td>
                  <td className="td-text-limited">
                    {row.paymentType || SETTLEMENT_PAYMENT_BREAKDOWN_MISSING_VALUE}
                  </td>
                  <td className="td-text-limited td-number-align-right">
                    {formatMoney(row.amount)}
                  </td>
                  <td className="td-text-limited">
                    {row.bank || SETTLEMENT_PAYMENT_BREAKDOWN_MISSING_VALUE}
                  </td>
                  <td className="td-text-limited">
                    {row.account || SETTLEMENT_PAYMENT_BREAKDOWN_MISSING_VALUE}
                  </td>
                  <td className="td-text-limited">
                    {row.number || SETTLEMENT_PAYMENT_BREAKDOWN_MISSING_VALUE}
                  </td>
                  <td className="td-text-limited">
                    {row.reference || SETTLEMENT_PAYMENT_BREAKDOWN_MISSING_VALUE}
                  </td>
                  <td className="td-text-limited text-center">{renderPaymentStatus(row.status)}</td>
                  <td className="td-text-limited text-center">
                    {row.images.length > 0 ? (
                      <IconButton
                        size="small"
                        onClick={() => setLightbox({ open: true, images: row.images, index: 0 })}
                        aria-label={`${SETTLEMENT_DETAILS_DEPOSITS_IMAGE_ARIA_PREFIX} ${row.images.length} ${
                          row.images.length > 1
                            ? SETTLEMENT_DETAILS_DEPOSITS_IMAGE_ARIA_PLURAL
                            : SETTLEMENT_DETAILS_DEPOSITS_IMAGE_ARIA_SINGULAR
                        } ${SETTLEMENT_PAYMENT_BREAKDOWN_IMAGE_ARIA_SUFFIX}`}
                        sx={{
                          border: `1px solid ${UI_COLOR_PRIMARY}`,
                          borderRadius: '4px',
                          color: UI_COLOR_PRIMARY_LINK,
                          backgroundColor: UI_COLOR_WHITE,
                          padding: '5px 8px',
                          '&:hover': {
                            backgroundColor: UI_COLOR_SURFACE_PRIMARY_SOFT
                          }
                        }}
                      >
                        <Badge
                          badgeContent={row.images.length}
                          max={99}
                          sx={{
                            '& .MuiBadge-badge': {
                              backgroundColor: UI_COLOR_ACCENT_ORANGE,
                              color: UI_COLOR_WHITE,
                              fontSize: 10,
                              fontWeight: 700,
                              minWidth: 18,
                              height: 18,
                              lineHeight: '18px',
                              padding: 0
                            }
                          }}
                        >
                          <GoogleIcon name="image" size={18} color={UI_COLOR_PRIMARY} />
                        </Badge>
                      </IconButton>
                    ) : (
                      SETTLEMENT_PAYMENT_BREAKDOWN_MISSING_VALUE
                    )}
                  </td>
                  <td className="td-text-limited text-center">
                    {row.files.length > 0 ? (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenAttachments(row.files)}
                        aria-label={`Ver ${row.files.length} adjunto${
                          row.files.length === 1 ? '' : 's'
                        } del pago`}
                        sx={{
                          border: `1px solid ${UI_COLOR_PRIMARY}`,
                          borderRadius: '4px',
                          color: UI_COLOR_PRIMARY_LINK,
                          backgroundColor: UI_COLOR_WHITE,
                          padding: '5px 8px',
                          '&:hover': {
                            backgroundColor: UI_COLOR_SURFACE_PRIMARY_SOFT
                          }
                        }}
                      >
                        <Badge
                          badgeContent={row.files.length}
                          max={99}
                          sx={{
                            '& .MuiBadge-badge': {
                              backgroundColor: UI_COLOR_ACCENT_ORANGE,
                              color: UI_COLOR_WHITE,
                              fontSize: 10,
                              fontWeight: 700,
                              minWidth: 18,
                              height: 18,
                              lineHeight: '18px',
                              padding: 0
                            }
                          }}
                        >
                          <GoogleIcon name="description" size={18} color={UI_COLOR_PRIMARY} />
                        </Badge>
                      </IconButton>
                    ) : (
                      SETTLEMENT_PAYMENT_BREAKDOWN_MISSING_VALUE
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11">{SETTLEMENT_PAYMENT_BREAKDOWN_EMPTY_STATE}</td>
              </tr>
            )}
          </tbody>
        </Table>
      </Paper>

      <EvidenceLightbox
        open={lightbox.open}
        images={lightbox.images}
        index={lightbox.index}
        onClose={() => setLightbox({ open: false, images: [], index: 0 })}
      />

      <Dialog
        open={attachmentsDialog.open}
        onClose={() => setAttachmentsDialog({ open: false, files: [] })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Adjuntos del pago</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            {attachmentsDialog.files.map((file, index) => (
              <Box
                key={file.url}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                <Typography noWrap title={file.title}>
                  {file.title || `Adjunto ${index + 1}`}
                </Typography>
                <Button
                  component="a"
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Abrir adjunto ${index + 1}`}
                  startIcon={<GoogleIcon name="open_in_new" size={16} />}
                >
                  Abrir
                </Button>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachmentsDialog({ open: false, files: [] })}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
